import { PERMISSION_CATEGORIES, ALL_RESOURCES, DEPENDENCIES } from "./constants";
import type { ModuleDef } from "./constants";

export type PermissionsMap = Record<string, string[]>;

export function getModuleActions(permissions: PermissionsMap, moduleId: string): string[] {
  return permissions[moduleId] || [];
}

export function countSelected(permissions: PermissionsMap): number {
  return ALL_RESOURCES.reduce((sum, r) => sum + (permissions[r] || []).length, 0);
}

export function countTotal(): number {
  return PERMISSION_CATEGORIES.reduce((sum, cat) =>
    sum + cat.modules.reduce((s, m) => s + m.actions.length, 0), 0
  );
}

export function countModuleActions(moduleId: string): number {
  for (const cat of PERMISSION_CATEGORIES) {
    const mod = cat.modules.find((m) => m.id === moduleId);
    if (mod) return mod.actions.length;
  }
  return 0;
}

export function countModuleEnabled(permissions: PermissionsMap, moduleId: string): number {
  return (permissions[moduleId] || []).length;
}

export function isModuleAllSelected(permissions: PermissionsMap, module: ModuleDef): boolean {
  const m = permissions[module.id] || [];
  return m.length === module.actions.length;
}

export function isModuleSomeSelected(permissions: PermissionsMap, module: ModuleDef): boolean {
  const m = permissions[module.id] || [];
  return m.length > 0 && m.length < module.actions.length;
}

export function toggleModuleAction(
  permissions: PermissionsMap,
  moduleId: string,
  action: string,
  checked: boolean
): PermissionsMap {
  const next = { ...permissions };
  const current = [...(next[moduleId] || [])];
  if (checked) {
    if (!current.includes(action)) current.push(action);
  } else {
    const idx = current.indexOf(action);
    if (idx >= 0) current.splice(idx, 1);
  }
  if (current.length > 0) {
    next[moduleId] = current;
  } else {
    delete next[moduleId];
  }
  return next;
}

export function toggleModuleAll(
  permissions: PermissionsMap,
  module: ModuleDef,
  checked: boolean
): PermissionsMap {
  const next = { ...permissions };
  if (checked) {
    next[module.id] = module.actions.map((a) => a.id);
  } else {
    delete next[module.id];
  }
  return next;
}

export function selectAllPermissions(): PermissionsMap {
  const p: PermissionsMap = {};
  for (const cat of PERMISSION_CATEGORIES) {
    for (const mod of cat.modules) {
      p[mod.id] = mod.actions.map((a) => a.id);
    }
  }
  return p;
}

export function clearAllPermissions(): PermissionsMap {
  return {};
}

export function applyDependencies(
  permissions: PermissionsMap,
  moduleId: string,
  action: string,
  checked: boolean
): PermissionsMap {
  let next = { ...permissions };

  if (checked) {
    const key = `${moduleId}.${action}`;
    const deps = DEPENDENCIES[key];
    if (deps) {
      for (const dep of deps) {
        const [depMod, depAction] = dep.split(".");
        const current = [...(next[depMod] || [])];
        if (!current.includes(depAction)) {
          current.push(depAction);
          next[depMod] = current;
        }
      }
    }
  }

  return next;
}

export function getAccessLevel(permissions: PermissionsMap): { level: string; color: string; icon: string } {
  const total = countSelected(permissions);
  if (total === 0) return { level: "None", color: "bg-gray-100 text-gray-600", icon: "minus" };

  const allResources = PERMISSION_CATEGORIES.flatMap((c) => c.modules);
  const hasFull = allResources.every((m) => {
    const a = permissions[m.id] || [];
    return m.actions.every((act) => a.includes(act.id));
  });
  if (hasFull) return { level: "Full Access", color: "bg-green-100 text-green-800", icon: "key" };

  const onlyView = allResources.every((m) => {
    const a = permissions[m.id] || [];
    return a.length === 1 && a.includes("view");
  });
  if (onlyView) return { level: "View Only", color: "bg-blue-100 text-blue-800", icon: "eye" };

  const hasCreate = allResources.some((m) => {
    const a = permissions[m.id] || [];
    return a.includes("create");
  });
  if (hasCreate) return { level: "Manager", color: "bg-amber-100 text-amber-800", icon: "shield" };

  return { level: "Limited", color: "bg-purple-100 text-purple-800", icon: "lock" };
}

export function filterPermissions(
  permissions: PermissionsMap,
  search: string
): PermissionsMap {
  if (!search) return permissions;
  const lower = search.toLowerCase();
  const filtered: PermissionsMap = {};
  for (const [moduleId, actions] of Object.entries(permissions)) {
    const matching = actions.filter((a) => a.toLowerCase().includes(lower));
    if (matching.length > 0) {
      filtered[moduleId] = matching;
    }
  }
  return filtered;
}

export function matchesSearch(moduleId: string, label: string, search: string): boolean {
  if (!search) return true;
  const lower = search.toLowerCase();
  return (
    moduleId.toLowerCase().includes(lower) ||
    label.toLowerCase().includes(lower)
  );
}

/**
 * Normalize permissions from API (flat `{ "module.action": boolean }` or `{ module: string[] }`)
 * to internal format `{ module: string[] }`.
 */
export function normalizePermissions(raw: Record<string, any> | null | undefined): PermissionsMap {
  if (!raw || typeof raw !== "object") return {};

  // Check if it's already the module-array format
  const values = Object.values(raw);
  if (values.some((v) => Array.isArray(v))) {
    const result: PermissionsMap = {};
    for (const [mod, actions] of Object.entries(raw)) {
      if (Array.isArray(actions) && actions.length > 0) {
        result[mod] = actions;
      }
    }
    return result;
  }

  // It's the flat format: { "module.action": true/false }
  const result: PermissionsMap = {};
  for (const [key, val] of Object.entries(raw)) {
    if (!val) continue;
    const dot = key.lastIndexOf(".");
    if (dot === -1) continue;
    const mod = key.slice(0, dot);
    const action = key.slice(dot + 1);
    if (!result[mod]) result[mod] = [];
    if (!result[mod].includes(action)) result[mod].push(action);
  }
  return result;
}

/**
 * Convert internal format `{ module: string[] }` to API flat format `{ "module.action": true }`.
 */
export function denormalizePermissions(perms: PermissionsMap): Record<string, boolean> {
  const result: Record<string, boolean> = {};
  for (const [mod, actions] of Object.entries(perms)) {
    for (const action of actions) {
      result[`${mod}.${action}`] = true;
    }
  }
  return result;
}

export function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "N/A";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(d));
  } catch {
    return "N/A";
  }
}

export function formatDateTime(d: string | Date | null | undefined): string {
  if (!d) return "N/A";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(d));
  } catch {
    return "N/A";
  }
}
